import {
  Refrigerator, WashingMachine, Wind, Zap, Tv, Sofa,
  Trees, ShieldCheck, Box, Flame, Droplets, Thermometer,
  Speaker, Wifi, Camera, DoorOpen, Sun, Wrench, Lightbulb,
  Monitor, Home, Microwave, CookingPot,
  UtensilsCrossed, AirVent, Waves, CircuitBoard, Router,
  Lock, BellRing, Laptop, Fan, Heater, Power, Armchair,
  Bed, Table2, Lamp, TreePine, Fence, Drill
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

// ─── TYPES ──────────────────────────────────────────────────────────────────

export interface Subcategory {
  id: string;
  label: string;
  icon: LucideIcon;
}

export interface PrimaryCategory {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  subcategories: Subcategory[];
}

export interface RoomLocation {
  id: string;
  label: string;
  icon: LucideIcon;
}

// ─── PRIMARY CATEGORIES ─────────────────────────────────────────────────────

export const PRIMARY_CATEGORIES: PrimaryCategory[] = [
  {
    id: "appliances",
    label: "Appliances",
    description: "Kitchen & laundry appliances",
    icon: UtensilsCrossed,
    subcategories: [
      { id: "refrigerator", label: "Refrigerator", icon: Refrigerator },
      { id: "washer", label: "Washer", icon: WashingMachine },
      { id: "dryer", label: "Dryer", icon: Wind },
      { id: "dishwasher", label: "Dishwasher", icon: Waves },
      { id: "range-stove", label: "Range / Stove", icon: CookingPot },
      { id: "microwave", label: "Microwave", icon: Microwave },
      { id: "freezer", label: "Freezer", icon: Thermometer },
      { id: "oven", label: "Oven", icon: Flame },
      { id: "other", label: "Other", icon: Box },
    ],
  },
  {
    id: "home-systems",
    label: "Home Systems",
    description: "HVAC, plumbing & electrical",
    icon: Home,
    subcategories: [
      { id: "air-conditioner", label: "Air Conditioner", icon: AirVent },
      { id: "furnace", label: "Furnace", icon: Flame },
      { id: "heat-pump", label: "Heat Pump", icon: Heater },
      { id: "thermostat", label: "Thermostat", icon: Thermometer },
      { id: "water-heater", label: "Water Heater", icon: Droplets },
      { id: "humidifier", label: "Humidifier", icon: Waves },
      { id: "air-purifier", label: "Air Purifier", icon: Fan },
      { id: "generator", label: "Generator", icon: Power },
      { id: "solar-panel", label: "Solar Panel", icon: Sun },
      { id: "electrical-panel", label: "Electrical Panel", icon: CircuitBoard },
      { id: "other", label: "Other", icon: Box },
    ],
  },
  {
    id: "electronics",
    label: "Electronics",
    description: "TVs, smart home & tech",
    icon: Monitor,
    subcategories: [
      { id: "tv", label: "TV", icon: Tv },
      { id: "sound-system", label: "Sound System", icon: Speaker },
      { id: "router", label: "Router / Modem", icon: Router },
      { id: "smart-hub", label: "Smart Home Hub", icon: Wifi },
      { id: "camera", label: "Security Camera", icon: Camera },
      { id: "doorbell", label: "Smart Doorbell", icon: DoorOpen },
      { id: "smart-lock", label: "Smart Lock", icon: Lock },
      { id: "laptop-desktop", label: "Computer", icon: Laptop },
      { id: "other", label: "Other", icon: Box },
    ],
  },
  {
    id: "furniture",
    label: "Furniture",
    description: "Indoor & outdoor furniture",
    icon: Sofa,
    subcategories: [
      { id: "sofa", label: "Sofa / Couch", icon: Sofa },
      { id: "bed", label: "Bed / Mattress", icon: Bed },
      { id: "table", label: "Table", icon: Table2 },
      { id: "chair", label: "Chair / Recliner", icon: Armchair },
      { id: "desk", label: "Desk", icon: Table2 },
      { id: "lamp", label: "Lamp / Light Fixture", icon: Lamp },
      { id: "cabinet", label: "Cabinet / Shelving", icon: Box },
    ],
  },
  {
    id: "outdoor",
    label: "Outdoor",
    description: "Garden, patio & tools",
    icon: Trees,
    subcategories: [
      { id: "lawn-mower", label: "Lawn Mower", icon: TreePine },
      { id: "grill", label: "Grill / BBQ", icon: Flame },
      { id: "pool-equipment", label: "Pool Equipment", icon: Waves },
      { id: "sprinkler", label: "Sprinkler System", icon: Droplets },
      { id: "patio-furniture", label: "Patio Furniture", icon: Fence },
      { id: "power-tools", label: "Power Tools", icon: Drill },
    ],
  },
  {
    id: "security",
    label: "Security",
    description: "Alarms, sensors & safety",
    icon: ShieldCheck,
    subcategories: [
      { id: "alarm-system", label: "Alarm System", icon: BellRing },
      { id: "security-camera", label: "Security Camera", icon: Camera },
      { id: "smart-lock-sec", label: "Smart Lock", icon: Lock },
      { id: "smoke-detector", label: "Smoke Detector", icon: Zap },
      { id: "motion-sensor", label: "Motion Sensor", icon: Lightbulb },
    ],
  },
];

// ─── ROOM LOCATIONS ─────────────────────────────────────────────────────────

export const ROOM_LOCATIONS: RoomLocation[] = [
  { id: "kitchen", label: "Kitchen", icon: UtensilsCrossed },
  { id: "bedroom", label: "Bedroom", icon: Bed },
  { id: "living-room", label: "Living Room", icon: Sofa },
  { id: "garage", label: "Garage", icon: Wrench },
  { id: "basement", label: "Basement", icon: Home },
  { id: "laundry-room", label: "Laundry Room", icon: WashingMachine },
  { id: "bathroom", label: "Bathroom", icon: Droplets },
  { id: "office", label: "Office", icon: Monitor },
  { id: "dining-room", label: "Dining Room", icon: UtensilsCrossed },
  { id: "attic", label: "Attic", icon: Home },
  { id: "outdoor", label: "Outdoor", icon: Trees },
  { id: "other", label: "Other", icon: Box },
];

// ─── ASSET TYPES BY SUBCATEGORY ─────────────────────────────────────────────

export const ASSET_TYPES_BY_SUBCATEGORY: Record<string, string[]> = {
  "refrigerator": ["French Door", "Side-by-Side", "Top Freezer", "Bottom Freezer", "Compact"],
  "washer": ["Top Load", "Front Load", "Compact", "Washer/Dryer Combo"],
  "dryer": ["Electric", "Gas", "Ventless", "Compact"],
  "dishwasher": ["Built-in", "Portable", "Drawer", "Compact"],
  "range-stove": ["Gas", "Electric", "Induction", "Dual Fuel"],
  "microwave": ["Countertop", "Over-the-Range", "Built-in", "Drawer"],
  "oven": ["Gas", "Electric", "Convection", "Wall Oven", "Double Oven"],
  "freezer": ["Upright", "Chest", "Compact"],
  "air-conditioner": ["Central", "Window Unit", "Portable", "Ductless Mini-Split"],
  "water-heater": ["Tank", "Tankless", "Heat Pump", "Solar"],
  "tv": ["LED/LCD", "OLED", "QLED", "Smart TV"],
};

// ─── POPULAR BRANDS BY CATEGORY ─────────────────────────────────────────────

export const BRANDS_BY_CATEGORY: Record<string, string[]> = {
  appliances: [
    "Samsung", "LG", "Whirlpool", "GE", "Bosch", "KitchenAid",
    "Maytag", "Frigidaire", "Kenmore", "Electrolux", "Miele",
    "Sub-Zero", "Viking", "Thermador", "Amana",
  ],
  "home-systems": [
    "Carrier", "Trane", "Lennox", "Rheem", "Goodman", "Daikin",
    "Honeywell", "Nest", "Ecobee", "Generac", "Kohler",
    "A.O. Smith", "Bradford White", "Aprilaire", "SunPower",
  ],
  electronics: [
    "Samsung", "LG", "Sony", "Apple", "Google", "Amazon",
    "Bose", "Sonos", "Ring", "Nest", "Arlo", "TP-Link",
    "Netgear", "Roku", "TCL", "Vizio", "August",
  ],
  furniture: [
    "IKEA", "Ashley", "Pottery Barn", "West Elm", "Crate & Barrel",
    "Restoration Hardware", "Wayfair", "La-Z-Boy", "Ethan Allen",
  ],
  outdoor: [
    "John Deere", "Husqvarna", "Honda", "Toro", "Weber",
    "Traeger", "Stihl", "EGO", "Craftsman", "DeWalt",
  ],
  security: [
    "Ring", "Nest", "SimpliSafe", "ADT", "Arlo", "Wyze",
    "August", "Yale", "Schlage", "First Alert", "Kidde",
  ],
};

// ─── WIZARD CONFIG ──────────────────────────────────────────────────────────

export const TOTAL_STEPS = 6;

export const STEP_LABELS = [
  "Category",
  "Type",
  "Identify",
  "Details",
  "Documents",
  "Done",
];

// Helper: map category ID → backend category string
export const CATEGORY_TO_BACKEND: Record<string, string> = {
  appliances: "Appliance",
  "home-systems": "HVAC",
  electronics: "Electronics",
  furniture: "Furniture",
  outdoor: "Outdoor",
  security: "Security",
};

// Build year list (current year down 25 years + "I don't remember")
export const PURCHASE_YEARS = (() => {
  const current = new Date().getFullYear();
  return Array.from({ length: 25 }, (_, i) => current - i);
})();
